import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, Calendar, Clock, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [booking, setBooking] = useState(null);
  const sessionId = searchParams.get("session_id");
  const bookingId = searchParams.get("booking_id");

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        setStatus("success");
        // Fetch booking details
        if (bookingId) {
          const bookingResponse = await axios.get(`${API}/bookings/${bookingId}`);
          setBooking(bookingResponse.data);
        }
      } else if (response.data.status === "expired") {
        setStatus("expired");
      } else {
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    }
  };

  return (
    <div className="pt-20 min-h-screen bg-slate-50">
      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="bg-white p-8 border border-slate-200 text-center">
          {status === "checking" && (
            <div data-testid="checking-status">
              <Loader2 size={60} className="mx-auto text-[#1a365d] animate-spin mb-6" />
              <h1 className="heading-lg text-[#0A0A0A] mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Confirming Payment...
              </h1>
              <p className="text-slate-600">
                Please wait while we verify your payment.
              </p>
            </div>
          )}

          {status === "success" && (
            <div data-testid="success-status">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={40} className="text-green-500" />
              </div>
              <h1 className="heading-lg text-[#0A0A0A] mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Booking Confirmed!
              </h1>
              <p className="text-slate-600 mb-8">
                Thank you for booking with Order & Clean Co. Your $100 deposit has been received.
              </p>

              {booking && (
                <div className="bg-slate-50 p-6 text-left mb-8">
                  <h2 className="font-bold uppercase tracking-wider text-sm mb-4">Booking Details</h2>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Calendar size={18} className="text-[#1a365d]" />
                      <span>{booking.preferred_date}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Clock size={18} className="text-[#1a365d]" />
                      <span>{booking.preferred_time}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <MapPin size={18} className="text-[#1a365d]" />
                      <span>{booking.customer_address}</span>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Deposit Paid</span>
                      <span className="font-bold text-green-600">$100</span>
                    </div>
                    <div className="flex justify-between mt-2">
                      <span className="text-slate-600">Balance Due</span>
                      <span className="font-bold">${booking.total_amount - 100}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-blue-50 border-l-4 border-[#1a365d] p-4 text-left mb-8">
                <h3 className="font-bold text-[#0A0A0A] mb-2">What's Next?</h3>
                <ol className="text-sm text-slate-600 space-y-2">
                  <li>1. You'll receive a confirmation email shortly</li>
                  <li>2. We'll send a reminder 24 hours before your appointment</li>
                  <li>3. Our team will arrive at your scheduled time</li>
                  <li>4. Remaining balance due after service completion</li>
                </ol>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/" data-testid="back-home-btn">
                  <Button className="btn-primary">
                    Back to Home
                  </Button>
                </Link>
                <Link to="/enhancements" data-testid="view-enhancements-btn">
                  <Button variant="outline" className="btn-secondary">
                    View Enhancements
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {status === "expired" && (
            <div data-testid="expired-status">
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock size={40} className="text-yellow-500" />
              </div>
              <h1 className="heading-lg text-[#0A0A0A] mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Session Expired
              </h1>
              <p className="text-slate-600 mb-8">
                Your payment session has expired. Please try booking again.
              </p>
              <Link to="/booking" data-testid="try-again-btn">
                <Button className="btn-primary">
                  Book Again
                </Button>
              </Link>
            </div>
          )}

          {status === "timeout" && (
            <div data-testid="timeout-status">
              <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock size={40} className="text-slate-500" />
              </div>
              <h1 className="heading-lg text-[#0A0A0A] mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Payment Verification
              </h1>
              <p className="text-slate-600 mb-8">
                We couldn't immediately verify your payment. If you completed the payment, 
                you'll receive a confirmation email shortly. If not, please try booking again.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/" data-testid="timeout-home-btn">
                  <Button className="btn-primary">
                    Back to Home
                  </Button>
                </Link>
                <Link to="/booking" data-testid="timeout-book-btn">
                  <Button variant="outline" className="btn-secondary">
                    Book Again
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingSuccessPage;
